/**
 * Teezano wishlist engine.
 *
 * Stores the list of saved product handles in localStorage (per browser/device,
 * no account required). Any element with [data-wishlist-toggle] and
 * [data-product-handle="..."] becomes a working wishlist button: click delegation
 * is attached once on `document`, so it works for buttons that exist on page load
 * AND for buttons injected later (Quick Add modal content, infinite scroll,
 * AJAX-loaded sections) without needing per-instance wiring.
 *
 * Fires a `wishlist:updated` CustomEvent on `document` (detail: { list }) whenever
 * the saved list changes, so other UI (header count badge, the wishlist page) can
 * react without polling.
 */
(function () {
  var STORAGE_KEY = 'teezano:wishlist';

  function readList() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // Storage unavailable (private browsing, quota, etc). Fail silently -
      // the UI just won't persist across reloads for this visit.
    }
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { list: list } }));
  }

  function isSaved(handle) {
    return readList().indexOf(handle) !== -1;
  }

  /** @returns {boolean} whether the handle is saved after the toggle */
  function toggle(handle) {
    if (!handle) return false;
    var list = readList();
    var idx = list.indexOf(handle);
    var nowSaved;
    if (idx === -1) {
      list.push(handle);
      nowSaved = true;
    } else {
      list.splice(idx, 1);
      nowSaved = false;
    }
    writeList(list);
    return nowSaved;
  }

  /** Removes a handle without toggling it back on if it wasn't there. */
  function remove(handle) {
    var list = readList();
    var idx = list.indexOf(handle);
    if (idx === -1) return;
    list.splice(idx, 1);
    writeList(list);
  }

  function syncButton(btn) {
    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    var saved = isSaved(handle);
    btn.classList.toggle('is-active', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }

  function syncAllButtons() {
    var buttons = document.querySelectorAll('[data-wishlist-toggle]');
    for (var i = 0; i < buttons.length; i++) syncButton(buttons[i]);
  }

  function updateCountBadges() {
    var count = readList().length;
    var badges = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = String(count);
      badges[i].hidden = count === 0;
    }
  }

  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();

    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    toggle(handle);

    // A product can appear more than once on a page (e.g. a card in a
    // carousel and the same card in Quick Add cache) - keep them all in sync.
    var escaped = window.CSS && CSS.escape ? CSS.escape(handle) : handle.replace(/"/g, '\\"');
    var siblings = document.querySelectorAll('[data-wishlist-toggle][data-product-handle="' + escaped + '"]');
    for (var i = 0; i < siblings.length; i++) syncButton(siblings[i]);
  });

  document.addEventListener('wishlist:updated', function () {
    syncAllButtons();
    updateCountBadges();
  });

  function init() {
    syncAllButtons();
    updateCountBadges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Wishlist buttons can also arrive after load (Quick Add modal morph,
  // infinite-scroll grids, section re-renders) - keep their initial state correct.
  var scheduled = false;
  var observer = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      syncAllButtons();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  window.TeezanoWishlist = {
    getHandles: readList,
    isSaved: isSaved,
    toggle: toggle,
    remove: remove,
  };
})();
